// src/app/features/markets/useMockMarketData.js
import { useEffect, useMemo, useRef, useState } from "react";

// simple deterministic-ish random walk per symbol
function seeded(symbol) {
  let h = 0;
  for (let i = 0; i < symbol.length; i++) h = (h * 31 + symbol.charCodeAt(i)) >>> 0;
  return h || 1;
}

function basePrice(symbol) {
  if (symbol === "BTCUSDT") return 45200;
  if (symbol === "ETHUSDT") return 2400;
  if (symbol === "SOLUSDT") return 95;
  if (symbol === "BNBUSDT") return 310;

  if (symbol === "EURUSD") return 1.09;
  if (symbol === "GBPUSD") return 1.27;
  if (symbol === "USDJPY") return 145.2;

  if (symbol === "XAUUSD") return 2045;
  if (symbol === "XAGUSD") return 24.1;

  return 100;
}

function formatPrice(symbol, px) {
  // crypto/metal mostly 2dp, FX often 4-5dp
  if (["EURUSD", "GBPUSD"].includes(symbol)) return px.toFixed(5);
  if (["USDJPY"].includes(symbol)) return px.toFixed(3);
  if (["XAUUSD", "XAGUSD"].includes(symbol)) return px.toFixed(2);
  return px.toFixed(2);
}

export function useMockMarketData(symbol, timeframe) {
  const [last, setLast] = useState(() => basePrice(symbol));
  const [changePct, setChangePct] = useState(0);
  const [series, setSeries] = useState(() => []);

  const seedRef = useRef(seeded(symbol));
  const openRef = useRef(basePrice(symbol));

  // reset on symbol/timeframe changes
  useEffect(() => {
    seedRef.current = seeded(symbol);
    const open = basePrice(symbol);
    openRef.current = open;

    // initialize series
    const init = [];
    let px = open;
    for (let i = 0; i < 60; i++) {
      // small walk
      const r = ((seedRef.current = (seedRef.current * 1664525 + 1013904223) >>> 0) / 2 ** 32) - 0.5;
      const step = (r * 0.006) * px; // 0.6% band
      px = Math.max(0.0001, px + step);
      init.push(px);
    }
    setSeries(init);
    setLast(px);
    setChangePct(((px - open) / open) * 100);
  }, [symbol, timeframe]);

  // live tick
  useEffect(() => {
    const iv = setInterval(() => {
      setSeries((prev) => {
        const px0 = prev[prev.length - 1] ?? basePrice(symbol);
        const r = ((seedRef.current = (seedRef.current * 1664525 + 1013904223) >>> 0) / 2 ** 32) - 0.5;
        const step = (r * 0.0025) * px0;
        const px = Math.max(0.0001, px0 + step);

        const next = prev.length >= 120 ? prev.slice(1) : prev.slice();
        next.push(px);

        setLast(px);
        const open = openRef.current || px;
        setChangePct(((px - open) / open) * 100);
        return next;
      });
    }, 1000);

    return () => clearInterval(iv);
  }, [symbol]);

  return useMemo(
    () => ({
      last,
      lastFormatted: formatPrice(symbol, last),
      changePct,
      changeFormatted: `${changePct >= 0 ? "+" : ""}${changePct.toFixed(2)}%`,
      series,
    }),
    [symbol, last, changePct, series]
  );
}
